// src/pages/admin/AdminUsersPage.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';
import { logAdminAction } from '../../services/adminLogService';
import AdminLogsPage from '../../pages/admin/AdminLogsPage';

const AdminUsersPage: React.FC = () => {
  const { notifySuccess, notifyError } = useNotification();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Fetch users from Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        notifyError('Failed to fetch users.');
        console.error(error);
      } else {
        setUsers(data || []);
        notifySuccess('Users loaded successfully!');
      }
      setLoading(false);
    };

    fetchUsers();
  }, [notifyError, notifySuccess]);

  // Filter users by search
  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(search.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(search.toLowerCase())
  );

  // A & B: Promote to Admin / Demote to User
  const handleRoleChange = async (userId: string, newRole: 'admin' | 'user', userName: string) => {
    const action = newRole === 'admin' ? 'promote' : 'demote';
    if (!window.confirm(`Are you sure you want to ${action} ${userName} to ${newRole}?`)) return;

    try {
      setActionLoadingId(userId);
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) {
        notifyError('Failed to update role.');
        console.error(error);
        return;
      }

      // Update local state
      setUsers(prev =>
        prev.map(u => (u.id === userId ? { ...u, role: newRole } : u))
      );

      // Log admin action
      await logAdminAction(
        'USER_ROLE_CHANGE',
        `${userName} role changed to ${newRole}`,
        { userId, newRole }
      );

      notifySuccess(`${userName} is now ${newRole}.`);
    } catch (err) {
      console.error(err);
      notifyError('Unexpected error occurred.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // C: Activate/Deactivate User
  const handleToggleActive = async (userId: string, currentStatus: boolean, userName: string) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';

    if (!window.confirm(`Are you sure you want to ${action} ${userName}?`)) return;

    try {
      setActionLoadingId(userId);
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: newStatus })
        .eq('id', userId);

      if (error) {
        notifyError(`Failed to ${action} user.`);
        console.error(error);
        return;
      }

      // Update local state
      setUsers(prev =>
        prev.map(u => (u.id === userId ? { ...u, is_active: newStatus } : u))
      );

      // Log admin action
      await logAdminAction(
        newStatus ? 'USER_ACTIVATE' : 'USER_DEACTIVATE',
        `${userName} was ${action}d`,
        { userId }
      );

      notifySuccess(`${userName} ${action}d successfully.`);
    } catch (err) {
      console.error(err);
      notifyError('Unexpected error occurred.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // E: Reset Password
  const handleResetPassword = async (userEmail: string, userName: string, userId: string) => {
    if (!window.confirm(`Send password reset email to ${userName} (${userEmail})?`)) return;

    try {
      setActionLoadingId(userId);
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        notifyError('Failed to send reset email.');
        console.error(error);
        return;
      }

      // Log admin action
      await logAdminAction(
        'PASSWORD_RESET',
        `Password reset link sent to ${userName}`,
        { userId, userEmail }
      );

      notifySuccess(`Reset email sent to ${userEmail}.`);
    } catch (err) {
      console.error(err);
      notifyError('Unexpected error occurred.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // F: View Full Profile
  const handleViewProfile = (user: any) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  return (
    <AdminLayout>
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">User Management</h1>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full border rounded px-3 py-2"
          />
        </div>

        {/* User List */}
        {loading ? (
          <p>Loading users...</p>
        ) : filteredUsers.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map(user => {
                    const displayName = user.full_name || 
                      (user.first_name && user.last_name 
                        ? `${user.first_name} ${user.last_name}` 
                        : user.email);

                    return (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                         <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {displayName}
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                              user.role === 'broker' ? 'bg-blue-100 text-blue-700'
                                : user.role === 'carrier' ? 'bg-green-100 text-green-700'
                                : user.role === 'super_admin' ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {user.role === 'broker' ? 'B' : user.role === 'carrier' ? 'C' : user.role === 'super_admin' ? 'SA' : 'U'}
                            </span>
                          </div>
                          {user.company_name && (
                            <div className="text-xs text-gray-500">{user.company_name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            user.role === 'broker' ? 'bg-blue-100 text-blue-700'
                              : user.role === 'carrier' ? 'bg-green-100 text-green-700'
                              : user.role === 'super_admin' ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {user.role || 'unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.is_active !== false
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col space-y-1">
                            <button
                              onClick={() => handleToggleActive(user.id, user.is_active !== false, displayName)}
                              disabled={actionLoadingId === user.id}
                              className="text-blue-600 hover:text-blue-900 disabled:opacity-50 text-left"
                            >
                              {user.is_active !== false ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleResetPassword(user.email, displayName, user.id)}
                              disabled={actionLoadingId === user.id}
                              className="text-purple-600 hover:text-purple-900 disabled:opacity-50 text-left"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleViewProfile(user)}
                              className="text-gray-600 hover:text-gray-900 text-left"
                            >
                              View Profile
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Profile Modal */}
        {showProfileModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold">User Profile</h2>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.full_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Role</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.role || 'user'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedUser.is_active !== false ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.company || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.phone_number || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Industry</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.industry || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Website</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.website || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedUser.street && selectedUser.city
                          ? `${selectedUser.street}, ${selectedUser.city}, ${selectedUser.state} ${selectedUser.zip_code}, ${selectedUser.country}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Verified</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedUser.is_verified ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedUser.created_at
                          ? new Date(selectedUser.created_at).toLocaleString()
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;